// © 2026 NM Software Development UG (haftungsbeschränkt)
// All rights reserved.

import {Component, OnInit, computed, inject, signal} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';

import {SelectComponent} from '../../../_ui/components/controls/select/select.component';
import {LoadingComponent} from '../../x-components/loading/loading.component';
import {SettingsComponent} from '../../legal/settings/settings.component';
import {
  TechnicalSecuritySystemComponent
} from '../../legal/technical-security-system/technical-security-system.component';
import {ShardHttpService} from '../../../owner/x-http-requests/shard-http.service';
import {CompanyHttpService} from '../../../owner/x-http-requests/company-http.service';
import {ShardDto} from '../../../models/admin_owner/shard/shard-dto';
import {CookieService} from '../../../_services/cookie.service';
import {CookieType} from '../../../x-models/x-enums/cookie-type';
import {describeAdministrationError} from '../../../_shared/errors/administration-errors';

/** One entry of either picker. `cqme-select`'s `TItem extends object`, so both halves need a real shape. */
export interface LegalContextOption {
  id: string;
  name: string;
}

/** The remembered pick, as stored under {@link OwnerLegalComponent.STORAGE_KEY}. */
interface RememberedPick {
  companyId?: string;
  shardId?: string;
}

/**
 * **Owner → Rechtliches** (#613/#631) — the fiscal setup of ONE location, for a `PlatformOwner`.
 *
 * ### Why it is a page and not a wizard step
 *
 * Both halves used to sit behind a "Rechtliches" step of the owner shard wizard (`owner/shard/edit`). That
 * was the wrong home and it showed: a wizard is a form with a save, and nothing on this screen is a field.
 * Choosing a provider is irreversible and typed-confirmed, re-initialising the TSE retires a legal signing
 * device, and every one of those actions commits the moment it is confirmed — so the wizard's
 * "Zurück / Weiter / Speichern" chrome promised a save that never applied to any of it, and the step had to
 * refuse itself entirely on an unsaved shard. As its own route the screen can say what it is about (one
 * location's fiscal chain), state the context at the top, and let each action carry its own confirmation.
 *
 * ### How the shard is chosen, in priority order
 *
 * 1. **`?shardId=` in the URL** — the deep link the Standorte list hands over, and the one an operator can
 *    paste into a ticket.
 * 2. **The remembered pick** (this browser only, `CookieType.Functional`). Support works one location for a
 *    while; re-picking it on every visit is friction with no upside.
 * 3. **Nothing** — the designed empty state, not a silent guess. Defaulting to "the first company's first
 *    shard" would put an irreversible screen in front of an arbitrary tenant.
 *
 * The query parameters are read **once**, from `route.snapshot`, and written back with `replaceUrl`. A live
 * `queryParams.subscribe` here would re-enter its own load on every pick (the trap the events list documents
 * in the style guide's *"Debounced, focus-preserving list search"*).
 *
 * ### One request for the shards, none on a company change
 *
 * `shardHttp.list('')` answers every shard on the installation with its company attached, so a deep-linked
 * `?shardId=` resolves its own company locally and switching company filters an in-memory list instead of
 * re-querying. `companyHttp.list()` is still asked for the company picker, because a company with no shard
 * yet must be pickable — the reader then gets a sentence saying so rather than an empty box.
 */
@Component({
  selector: 'app-owner-legal',
  standalone: true,
  templateUrl: './owner-legal.component.html',
  imports: [SelectComponent, LoadingComponent, SettingsComponent, TechnicalSecuritySystemComponent]
})
export class OwnerLegalComponent implements OnInit {
  /** `CookieType.Functional`, so a browser that declined functional storage simply never remembers. */
  static readonly STORAGE_KEY = 'cqme.owner.legal.last-pick';

  private readonly shardHttp = inject(ShardHttpService);
  private readonly companyHttp = inject(CompanyHttpService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  protected readonly loading = signal(true);
  /** The German sentence for a failed context load. The two pickers are useless without it, so it replaces them. */
  protected readonly loadError = signal<string | undefined>(undefined);

  protected readonly companies = signal<LegalContextOption[]>([]);
  private readonly allShards = signal<ShardDto[]>([]);

  protected readonly companyId = signal<string | undefined>(undefined);
  protected readonly shardId = signal<string | undefined>(undefined);

  /** The chosen company's shards, in the order the server listed them. */
  protected readonly shards = computed<LegalContextOption[]>(() => {
    const company = this.companyId();
    if (!company) return [];
    return this.allShards()
      .filter(shard => shard.company?.id === company)
      .map(shard => ({id: shard.id, name: shard.name}));
  });

  /** The picked shard itself — the page header's context line, and what proves the id still exists. */
  protected readonly shard = computed<ShardDto | undefined>(() => {
    const id = this.shardId();
    return id ? this.allShards().find(candidate => candidate.id === id) : undefined;
  });

  /** A company was picked and it owns no location — a real state, and the one the empty box would hide. */
  protected readonly companyHasNoShards = computed(() =>
    this.companyId() !== undefined && this.shards().length === 0);

  ngOnInit(): void {
    this.load();
  }

  /** (Re)loads both lists and then applies the deep link / remembered pick to whatever came back. */
  protected load(): void {
    this.loading.set(true);
    this.loadError.set(undefined);

    // Deliberately sequential rather than a `forkJoin`: the shard list is the one that decides the pick, and
    // a failure of either leaves the page in the same state — the error card, with no pickers.
    this.companyHttp.list().subscribe({
      next: companies => {
        this.companies.set(companies.map(company => ({id: company.id, name: company.name})));
        this.shardHttp.list('').subscribe({
          next: shards => {
            this.allShards.set(shards);
            this.applyInitialPick();
            this.loading.set(false);
          },
          error: error => this.failContext(error)
        });
      },
      error: error => this.failContext(error)
    });
  }

  /** Company picked: the shard goes with it, because a shard of the previous company is not a valid pick. */
  protected chooseCompany(companyId: string | undefined): void {
    this.companyId.set(companyId);
    this.shardId.set(undefined);
    this.remember();
    this.syncUrl();
  }

  protected chooseShard(shardId: string | undefined): void {
    this.shardId.set(shardId);
    this.remember();
    this.syncUrl();
  }

  /** `cqme-select` declares `TValue | TValue[] | undefined` even single-valued — normalise once, here. */
  protected one(value: string | string[] | undefined): string | undefined {
    return Array.isArray(value) ? value[0] : value;
  }

  private failContext(error: unknown): void {
    this.loading.set(false);
    console.error(error);
    // `ShardService`/`CompanyService` register no `ErrorCatalog` codes, so the call-site sentence IS the
    // message here (see `administration-errors.ts`'s header).
    this.loadError.set(describeAdministrationError(error,
      'Unternehmen und Standorte konnten nicht geladen werden. Bitte laden Sie die Liste erneut.'));
  }

  /**
   * URL first, then the remembered pick — and a shard id that no longer exists is dropped rather than shown.
   * A stale id would otherwise send both legal components at a shard the server answers 404/403 for, which
   * reads as an outage instead of as "that location is gone".
   */
  private applyInitialPick(): void {
    const fromUrl = this.route.snapshot.queryParamMap.get('shardId') ?? undefined;
    const remembered = this.readRemembered();
    const wanted = fromUrl ?? remembered.shardId;

    const shard = wanted ? this.allShards().find(candidate => candidate.id === wanted) : undefined;
    if (shard) {
      this.companyId.set(shard.company?.id);
      this.shardId.set(shard.id);
      return;
    }

    // No (valid) shard. A remembered COMPANY still narrows the second picker, which is most of the value.
    const company = remembered.companyId;
    if (company && this.companies().some(candidate => candidate.id === company)) this.companyId.set(company);
  }

  private readRemembered(): RememberedPick {
    try {
      // `getItem` is not trapped by `CookieService`'s privacy guard; only writes go through it.
      const raw = localStorage.getItem(OwnerLegalComponent.STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as RememberedPick;
      return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch {
      // A private window, blocked site data or a value somebody hand-edited: remember nothing, render fine.
      return {};
    }
  }

  private remember(): void {
    const pick: RememberedPick = {companyId: this.companyId(), shardId: this.shardId()};
    // NEVER `localStorage.setItem` — `CookieService` replaces the global with one that throws (see its
    // constructor). `Functional`, because this is a convenience, not something the page needs to work.
    this.cookieService.setLocalStorage(CookieType.Functional, OwnerLegalComponent.STORAGE_KEY,
      JSON.stringify(pick));
  }

  /** Keeps `?shardId=` shareable. `replaceUrl`, so picking around does not fill the back button. */
  private syncUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {shardId: this.shardId() ?? null},
      queryParamsHandling: 'merge',
      replaceUrl: true
    }).then();
  }
}
